import { TFunction } from "i18next";

import { IUserPreferences } from "../@types/Preferences";
import { persistedCopyT } from "../config/persistedCopy";
import { AUTH_UNIT_PREFERENCES } from "../constants/localStorageKeys";
import { safeJsonParse, safeStorage } from "./storage";
import { TRIGGER_TYPE_LABEL_KEYS } from "./triggerLabels";

/** The user's weight unit, read where the Create trigger form reads it. */
export function getStoredWeightUnit(): string | undefined {
  return safeJsonParse<IUserPreferences | undefined>(
    safeStorage.getItem(AUTH_UNIT_PREFERENCES),
    undefined
  )?.weightUnit;
}

/**
 * A trigger's summary sentence ("An alert will be sent when your patient's …"), built from the
 * Create trigger form's values.
 *
 * Moved out of CreateTriggerModal unchanged (EN-5284), so the Manage triggers table can show a
 * stored summary in the user's language: the modal still writes it — in en-GB, as hcpMessage —
 * and the table rebuilds it from the trigger's values.
 */
export interface ITriggerSummaryInput {
  type: string;
  /** A COMPARATORS value: "greater_than", "percentage_decrease", "weight_gain"… */
  comparator: string;
  /** The threshold, or the baseline for a percentage comparator, as typed. */
  threshold: string;
  percentage: string;
  /** The weight gain time frame in days, as typed. */
  timeframe: string;
  /** An alert window value: "any_reading", "highest_of_day", "consecutive_highest_3_days"… */
  alert: string;
  unit: string;
  additionalMeasurement: boolean;
  type2: string;
  comparator2: string;
  threshold2: string;
  percentage2: string;
  unit2: string;
}

/**
 * How a comparator reads inside the summary sentence — "greater than", not the dropdown's
 * "greater than (>)". The sentence used to reuse the dropdown label with its parenthetical
 * stripped by a regex, which only held while every label was English and carried the symbol in
 * brackets. These four are the only comparators that reach the sentence: weight gain and the two
 * percentage comparators take their own branches, so a missing entry here is what tells the
 * builder to use the percentage wording.
 */
const SUMMARY_COMPARATOR_KEYS: Record<string, string> = {
  greater_than: "patientPage_triggers_summary_comparator_greaterThan",
  greater_or_equal: "patientPage_triggers_summary_comparator_greaterOrEqual",
  less_than: "patientPage_triggers_summary_comparator_lessThan",
  less_or_equal: "patientPage_triggers_summary_comparator_lessOrEqual",
};

/**
 * The unit the trigger form labels a measurement's threshold with: the user's weight unit for
 * weight, the measurement's default unit for the rest (which is also the unit it is saved in).
 * Undefined for a measurement the form does not offer.
 */
export function getTriggerUnit(
  measurementField: string,
  weightUnit: string | undefined
): string | undefined {
  switch (measurementField) {
    case "weight":
      return weightUnit ?? "kg";
    case "height":
      return "cm";
    case "bp_systolic":
    case "bp_diastolic":
      return "mmHg";
    case "hemoglobin":
      return "g/dL";
    case "cni_trough":
      return "ng/ml";
    case "blood_glucose":
      return "mmol/L";
    case "heart_rate":
      return "BPM";
    case "pulse_ox":
      return "%";
    case "temperature":
      return "°C";
    case "dyspnea":
      return "0-4";
    case "fvc":
    case "fev1":
      return "L";
    case "fev25_75":
      return "L/s";
    default:
      return undefined;
  }
}

const buildMeasurementPhrase = (
  translate: TFunction,
  typeValue: string,
  alert: string
): string | undefined => {
  const labelKey = TRIGGER_TYPE_LABEL_KEYS[typeValue];

  if (!labelKey) return undefined;

  return translate(
    alert === "any_reading"
      ? "patientPage_triggers_summary_measurementAny"
      : "patientPage_triggers_summary_measurementHighest",
    { type: translate(labelKey) }
  );
};

/**
 * One "<measurement> is <comparison>" clause. The `NoType` variants exist because the second
 * measurement's type can still be empty while the summary is on screen — the sentence renders
 * half-written as the user fills the form in, exactly as it did before.
 */
const buildComparisonPhrase = (
  translate: TFunction,
  alert: string,
  typeValue: string,
  comparatorValue: string,
  thresholdValue: string,
  percentageValue: string,
  unit: string
): string => {
  const measurement = buildMeasurementPhrase(translate, typeValue, alert);
  // Breathlessness is a 0-4 scale; its "unit" is never appended to the number.
  const value = `${thresholdValue}${typeValue !== "dyspnea" ? unit : ""}`;
  const comparatorKey = SUMMARY_COMPARATOR_KEYS[comparatorValue];

  if (comparatorKey) {
    return translate(
      measurement
        ? "patientPage_triggers_summary_conditionThreshold"
        : "patientPage_triggers_summary_conditionThresholdNoType",
      { measurement, comparator: translate(comparatorKey), value }
    );
  }

  const direction =
    comparatorValue === "percentage_decrease" ? "Less" : "Greater";

  return translate(
    measurement
      ? `patientPage_triggers_summary_conditionPercent${direction}`
      : `patientPage_triggers_summary_conditionPercent${direction}NoType`,
    { measurement, percent: percentageValue, value }
  );
};

/**
 * Builds the whole sentence in `translate`'s language.
 *
 * Every part the reader sees is a key with slots rather than a fragment glued on with `+`, so a
 * translator can reorder the clause inside the sentence and the sentence inside its suffix. The
 * en-GB values reproduce the old concatenation word for word.
 */
export function buildTriggerSummarySentence(
  translate: TFunction,
  input: ITriggerSummaryInput
): string {
  const { comparator, alert } = input;

  if (comparator === "weight_gain") {
    const days = Number.parseInt(input.timeframe, 10);

    return translate("patientPage_triggers_summary_weightGain", {
      count: Number.isNaN(days) ? 0 : days,
      threshold: input.threshold,
      unit: input.unit,
      // The raw value, not the label: weight gain only applies to weight, and this reads
      // "…or more in weight over 3 days" as it always has.
      type: input.type,
      timeframe: input.timeframe,
    });
  }

  let condition = buildComparisonPhrase(
    translate,
    alert,
    input.type,
    comparator,
    input.threshold,
    input.percentage,
    input.unit
  );

  if (input.additionalMeasurement) {
    condition = translate("patientPage_triggers_summary_and", {
      first: condition,
      second: buildComparisonPhrase(
        translate,
        alert,
        input.type2,
        input.comparator2,
        input.threshold2,
        input.percentage2,
        input.unit2
      ),
    });
  }

  // The two consecutive-day suffixes read differently after a threshold clause ("… each day
  // for 3 consecutive days") and after a percentage one ("…, for 3 consecutive days").
  const usesThresholdWording = Boolean(SUMMARY_COMPARATOR_KEYS[comparator]);

  if (
    alert === "consecutive_highest_3_days" ||
    alert === "consecutive_highest_7_days"
  ) {
    condition = translate(
      usesThresholdWording
        ? "patientPage_triggers_summary_consecutiveDays"
        : "patientPage_triggers_summary_consecutiveDaysPercent",
      {
        condition,
        count: alert === "consecutive_highest_3_days" ? 3 : 7,
      }
    );
  }

  if (alert === "highest_of_day") {
    condition = translate("patientPage_triggers_summary_oneDay", {
      condition,
    });
  }

  return translate("patientPage_triggers_summary_sentence", { condition });
}

/** The form's comparator for a stored one: the symbol, and whether it compares to a baseline. */
function comparatorFromStored(
  symbol: string | undefined,
  isPercentage: boolean
): string | undefined {
  switch (symbol) {
    case ">":
      return isPercentage ? "percentage_increase" : "greater_than";
    case "<":
      return isPercentage ? "percentage_decrease" : "less_than";
    case ">=":
      return isPercentage ? undefined : "greater_or_equal";
    case "<=":
      return isPercentage ? undefined : "less_or_equal";
    case "+":
      return isPercentage ? undefined : "weight_gain";
    default:
      return undefined;
  }
}

/** The form's alert window for a stored time frame, as the edit modal reads it. */
function alertFromDays(days: number | undefined): string {
  switch (days) {
    case 1:
      return "highest_of_day";
    case 3:
      return "consecutive_highest_3_days";
    case 7:
      return "consecutive_highest_7_days";
    default:
      return "any_reading";
  }
}

const asText = (value: number | undefined) =>
  value === undefined || value === null ? undefined : String(value);

/** What the API sends for one row of the Manage triggers table, as far as the summary needs. */
export interface ITriggerSummaryRow {
  hcpMessage: string;
  measurementField?: string;
  measurementField2?: string;
  comparator?: string;
  comparator2?: string;
  timeframeDays?: number;
  threshold?: number;
  baseline?: number;
  percentChange?: number;
  threshold2?: number;
  baseline2?: number;
  percentChange2?: number;
}

function toSummaryInput(
  row: ITriggerSummaryRow,
  weightUnit: string | undefined
): ITriggerSummaryInput | undefined {
  if (!row.measurementField) return undefined;

  const isPercentage =
    row.percentChange !== undefined && row.percentChange !== null;
  const comparator = comparatorFromStored(row.comparator, isPercentage);
  const threshold = asText(isPercentage ? row.baseline : row.threshold);
  const unit = getTriggerUnit(row.measurementField, weightUnit);

  if (!comparator || threshold === undefined || unit === undefined) {
    return undefined;
  }

  const additionalMeasurement = Boolean(row.measurementField2);
  let comparator2 = "";
  let threshold2 = "";
  let unit2 = "";

  if (row.measurementField2) {
    const isPercentage2 =
      row.percentChange2 !== undefined && row.percentChange2 !== null;
    const second = comparatorFromStored(row.comparator2, isPercentage2);
    const secondThreshold = asText(
      isPercentage2 ? row.baseline2 : row.threshold2
    );
    const secondUnit = getTriggerUnit(row.measurementField2, weightUnit);

    if (!second || secondThreshold === undefined || secondUnit === undefined) {
      return undefined;
    }

    comparator2 = second;
    threshold2 = secondThreshold;
    unit2 = secondUnit;
  }

  const days = row.timeframeDays ?? undefined;

  return {
    type: row.measurementField,
    comparator,
    threshold,
    percentage: asText(row.percentChange) ?? "",
    timeframe: asText(days) ?? "",
    alert: alertFromDays(days),
    unit,
    additionalMeasurement,
    type2: row.measurementField2 ?? "",
    comparator2,
    threshold2,
    percentage2: asText(row.percentChange2) ?? "",
    unit2,
  };
}

/**
 * EN-5284: a stored trigger summary, in the user's language.
 *
 * The summary is stored in en-GB (hcpMessage) on purpose — the alert notification and every user
 * read the same record — so it is only ever translated for display. The sentence is rebuilt from
 * the trigger's values with the same builder the Create trigger modal stores it with, and the
 * rebuild is only shown if its en-GB version is exactly the stored text. Anything else — a summary
 * written before this wording, a number typed as "4.50" that comes back as 4.5, a unit preference
 * changed since — keeps the stored text, so a row never shows a sentence that says something
 * different from what was saved.
 */
export function formatTriggerSummary(
  t: TFunction,
  row: ITriggerSummaryRow,
  weightUnit: string | undefined
): string {
  const input = toSummaryInput(row, weightUnit);

  if (!input) return row.hcpMessage;

  if (buildTriggerSummarySentence(persistedCopyT, input) !== row.hcpMessage) {
    return row.hcpMessage;
  }

  return buildTriggerSummarySentence(t, input);
}
